import { THUMBNAILS_MODE_PARAM } from '~/common/consts';
import { actionBlockTypes } from '~/common/flows.constants';
import { CXPage } from '~/components/ClientExperience/OneFlow/models/cx-page.model';
import {
    BLOCK_ID_QUERY_PARAM,
    TPageThumbnail,
    TPageThumbnails
} from '~/components/ClientExperience/components/PublicTemplateThumbnails/types';
import { i18n } from '~/i18n/config';
import { NAMESPACES } from '~/i18n/namespaces';
import { TCXBlock } from '~/types/client-experience.types';

const INTERACTIVE_BLOCKS = [
    'charge_container_block',
    'contract_block',
    'invoice_container_block',
    'schedule_container_block',
    'service_selection_block',
    'question_block',
    'services_block'
];

const searchParams = new URLSearchParams(window.location.search);

const isInteractiveBlock = (block: TCXBlock) => {
    if (block.type === 'container_block') {
        return block.childBlocks.some(isInteractiveBlock);
    }
    return INTERACTIVE_BLOCKS.includes(block.type);
};

export const getElement = <T = Element>(selector: string): Promise<T> => {
    const findElement = (resolve: (value: T) => void) => {
        const element = <T>document.querySelector(selector);
        if (element) {
            resolve(element);
        } else {
            setTimeout(findElement, 300, resolve);
        }
    };

    return new Promise(resolve => findElement(resolve));
};

const getFirstInteractiveBlock = (page: CXPage) =>
    page.rootBlock.childBlocks.find(isInteractiveBlock);

const getAllInteractiveBlocks = (page: CXPage) =>
    page.rootBlock.childBlocks.filter(isInteractiveBlock);

export const getFirstContainerId = (page: CXPage) =>
    page.rootBlock.childBlocks[0].block_id;

const loadImages = (pageThumbnails: string[]): Promise<HTMLImageElement>[] => {
    return pageThumbnails.map(
        pageMainThumbnail =>
            new Promise(resolve => {
                const image = new Image();
                image.src = pageMainThumbnail;
                image.onload = () => resolve(image);
            })
    );
};

export const getFullTemplateImageLink = async pageThumbnails => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let yOffset: number = 0;
    const pagesMainThumbnails = pageThumbnails
        .filter(({ pageMainThumbnail }) => !!pageMainThumbnail)
        .map(({ pageMainThumbnail }) => pageMainThumbnail);

    const images = await Promise.all(loadImages(pagesMainThumbnails));
    const totalHeight = images.reduce((sum, image) => sum + image.height, 0);
    const maxWidth = Math.max(...images.map(image => image.width));
    canvas.width = maxWidth;
    canvas.height = totalHeight;

    images.forEach(image => {
        ctx.drawImage(image, 0, yOffset);
        yOffset += image.height;
    });
    return canvas.toDataURL('image/png');
};

export const handleScrollToBlock = async (blockId: string) => {
    const blockElement = await getElement(`[data-block-id="${blockId}"]`);
    blockElement.scrollIntoView();
    setTimeout(() => document.body.classList.add('block_visible'), 500);
};

const t = i18n.getFixedT('en', NAMESPACES.GLOBALS);

export const getBlocksForCapture = ({
    page,
    flowHasOnePage
}: {
    page;
    flowHasOnePage;
}) => {
    const firstContainerId = getFirstContainerId(page);
    const interactiveBlocks = flowHasOnePage
        ? getAllInteractiveBlocks(page)
        : [getFirstInteractiveBlock(page)];

    return [
        { id: firstContainerId, title: page.title },
        ...interactiveBlocks
            .filter(block => block && block.block_id !== firstContainerId)
            .map(block => ({
                id: block.block_id,
                title:
                    block.type !== 'container_block'
                        ? t(actionBlockTypes[block.type])
                        : 'Question'
            }))
    ];
};

export const getPagesThumbnailsAfterUploadValues = async (
    pagesThumbnailsAfterUploadPromises: {
        pageTitle: string;
        thumbnails: Promise<TPageThumbnail>[];
    }[]
) => {
    const pagesThumbnailsAfterUpload: {
        pageTitle: string;
        thumbnails: TPageThumbnails;
    }[] = [];

    for (const {
        pageTitle,
        thumbnails
    } of pagesThumbnailsAfterUploadPromises) {
        try {
            const values = await Promise.all(thumbnails);
            pagesThumbnailsAfterUpload.push({ pageTitle, thumbnails: values });
        } catch (error) {
            throw error;
        }
    }

    return pagesThumbnailsAfterUpload;
};

export const arrayBufferToBase64 = buffer => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
};

export const shouldRunCapturingThumbnail = location.search.includes(
    THUMBNAILS_MODE_PARAM
);

export const targetBlockIdForThumbnail = searchParams.get(BLOCK_ID_QUERY_PARAM);

export const isCapturingThumbnailMode =
    !!targetBlockIdForThumbnail || shouldRunCapturingThumbnail;

export const MAXIMUM_PAGES_FOR_FULL_SCREENSHOT = 3;

export const PUBLIC_TEMPLATE_THUMBNAILS_STORAGE_KEY =
    'public_template_thumbnails';
